// src/components/game/CodeRunner.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScore } from '../../context/ScoreContext';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

const CodeRunner = ({ isDarkMode }) => {
  const { updateScore, getScore } = useScore();
  const [bestScore, setBestScore] = useState(getScore('code-runner'));

  const [code, setCode] = useState('// დაწერეთ JavaScript კოდი\nconsole.log("Hello, World!");');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(0);
  const [executionCount, setExecutionCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [fontSize, setFontSize] = useState(14);

  const runCode = () => {
    setIsRunning(true);
    setExecutionCount(prev => prev + 1);
    const startTime = performance.now();
    
    const logs = [];
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.log = (...args) => {
      logs.push({ type: 'log', message: args.join(' ') });
      originalLog(...args);
    };
    console.error = (...args) => {
      logs.push({ type: 'error', message: args.join(' ') });
      originalError(...args);
    };
    console.warn = (...args) => {
      logs.push({ type: 'warn', message: args.join(' ') });
      originalWarn(...args);
    };

    try {
      const result = new Function(code)();
      const endTime = performance.now();
      const duration = ((endTime - startTime) / 1000).toFixed(3);
      
      let outputText = '';
      if (logs.length > 0) {
        outputText = logs.map(log => {
          const prefix = log.type === 'error' ? '❌ ' : log.type === 'warn' ? '⚠️ ' : '';
          return `${prefix}${log.message}`;
        }).join('\n');
      } else if (result !== undefined) {
        outputText = `📤 Return: ${String(result)}`;
      } else {
        outputText = '✅ კოდი წარმატებით შესრულდა!';
      }
      
      outputText += `\n\n⏱️ შესრულების დრო: ${duration} წამი`;
      setOutput(outputText);
      
      // ქულის გამოთვლა
      const complexity = Math.min(10, Math.floor(code.split('\n').length * 1.5));
      const hasLogs = logs.length > 0 ? 2 : 0;
      const hasReturn = result !== undefined ? 1 : 0;
      const bonus = duration < 0.1 ? 2 : 0;
      const newScore = Math.min(20, complexity + hasLogs + hasReturn + bonus);
      setScore(newScore);
      if (newScore > bestScore) {
        updateScore('code-runner', newScore);
        setBestScore(getScore('code-runner'));
      }
      setErrorCount(prev => prev);
    } catch (error) {
      setOutput(`❌ შეცდომა:\n${error.message}\n\n📖 ${error.stack || ''}`);
      setErrorCount(prev => prev + 1);
    } finally {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
      setIsRunning(false);
      setTime(prev => prev + 1);
    }
  };

  const clearCode = () => {
    setCode('');
    setOutput('');
    setScore(0);
  };

  const resetCode = () => {
    setCode('// დაწერეთ JavaScript კოდი\nconsole.log("Hello, World!");');
    setOutput('');
    setScore(0);
  };

  const examples = [
    { 
      name: 'Hello World', 
      code: 'console.log("Hello, World!");',
      desc: 'მარტივი Hello World'
    },
    { 
      name: 'Fibonacci', 
      code: 'function fib(n) {\n  if (n <= 1) return n;\n  return fib(n-1) + fib(n-2);\n}\nconsole.log(fib(10));',
      desc: 'ფიბონაჩის მიმდევრობა'
    },
    { 
      name: 'Array Map', 
      code: 'const numbers = [1, 2, 3, 4, 5];\nconst doubled = numbers.map(n => n * 2);\nconsole.log(doubled);',
      desc: 'მასივის ტრანსფორმაცია'
    },
    { 
      name: 'FizzBuzz', 
      code: 'for (let i = 1; i <= 15; i++) {\n  if (i % 15 === 0) console.log("FizzBuzz");\n  else if (i % 3 === 0) console.log("Fizz");\n  else if (i % 5 === 0) console.log("Buzz");\n  else console.log(i);\n}',
      desc: 'კლასიკური FizzBuzz'
    },
    { 
      name: 'Palindrome', 
      code: 'function isPalindrome(str) {\n  return str === str.split("").reverse().join("");\n}\nconsole.log(isPalindrome("radar"));\nconsole.log(isPalindrome("hello"));',
      desc: 'პალინდრომის შემოწმება'
    }
  ];

  const loadExample = (exampleCode) => {
    setCode(exampleCode);
    setOutput('');
    setScore(0);
  };

  const getScoreEmoji = () => {
    if (score >= 15) return '🏆';
    if (score >= 10) return '⭐';
    if (score >= 5) return '💪';
    return '📈';
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: '850px',
    width: '100%',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    padding: 'clamp(20px, 4vw, 30px)',
    borderRadius: '20px',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out'
  };

  const textareaStyle = {
    width: '100%',
    minHeight: '200px',
    padding: 'clamp(12px, 2vw, 15px)',
    fontFamily: 'monospace',
    fontSize: `${fontSize}px`,
    borderRadius: '12px',
    border: '2px solid',
    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    background: isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.02)',
    color: isDarkMode ? '#4ade80' : '#1a1a2e',
    outline: 'none',
    resize: 'vertical',
    tabSize: 2,
    transition: 'all 0.3s',
    lineHeight: '1.6'
  };

  const outputStyle = {
    width: '100%',
    minHeight: '120px',
    maxHeight: '300px',
    padding: 'clamp(12px, 2vw, 15px)',
    fontFamily: 'monospace',
    fontSize: `${fontSize - 2}px`,
    borderRadius: '12px',
    border: '2px solid',
    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    background: isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.02)',
    color: isDarkMode ? '#fff' : '#000',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    lineHeight: '1.8'
  };

  const buttonStyle = {
    padding: 'clamp(10px, 1.5vw, 14px) clamp(16px, 2vw, 24px)',
    borderRadius: '12px',
    border: 'none',
    fontSize: 'clamp(13px, 1.5vw, 15px)',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          .game-title {
            background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          .output-error {
            color: #f87171 !important;
          }
          .output-success {
            color: #4ade80 !important;
          }
        `}
      </style>

      <div style={containerStyle}>
        <Link to="/games" style={{
          display: 'inline-block',
          padding: '8px 20px',
          marginBottom: '20px',
          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          color: isDarkMode ? '#fff' : '#000',
          textDecoration: 'none',
          borderRadius: '12px',
          transition: 'all 0.3s',
          backdropFilter: 'blur(10px)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateX(-5px)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateX(0)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
        }}>
          ← უკან
        </Link>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h1 style={{ margin: 0, fontSize: 'clamp(22px, 3vw, 32px)' }}>
            <span className="game-title">⚡ Code Runner</span>
          </h1>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold' }}>
              🏆 {bestScore}
            </span>
            <span style={{ color: isDarkMode ? '#aaa' : '#666' }}>
              🎯 {score}
            </span>
          </div>
        </div>

        <p style={{ 
          color: isDarkMode ? '#aaa' : '#666', 
          marginBottom: '20px',
          fontSize: 'clamp(13px, 1.5vw, 16px)'
        }}>
          დაწერეთ და გაუშვით JavaScript კოდი
        </p>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
          gap: '8px',
          marginBottom: '20px',
          padding: '15px',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: '18px' }}>📊</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{executionCount}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>გაშვება</div>
          </div>
          <div>
            <div style={{ fontSize: '18px' }}>⏱️</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{time}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>ჯერ</div>
          </div>
          <div>
            <div style={{ fontSize: '18px', color: '#f87171' }}>❌</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{errorCount}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>შეცდომა</div>
          </div>
          <div>
            <div style={{ fontSize: '18px' }}>{getScoreEmoji()}</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{score}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>ქულა</div>
          </div>
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '15px',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '12px', color: isDarkMode ? '#666' : '#999' }}>
            📐 {fontSize}px
          </span>
          <input
            type="range"
            min="10"
            max="20"
            value={fontSize}
            onChange={(e) => setFontSize(parseInt(e.target.value))}
            style={{
              width: '80px',
              accentColor: '#667eea'
            }}
          />
          <button
            onClick={() => setShowLineNumbers(!showLineNumbers)}
            style={{
              ...buttonStyle,
              background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              color: isDarkMode ? '#fff' : '#000',
              padding: '6px 12px',
              fontSize: '12px'
            }}
          >
            {showLineNumbers ? '🔢' : '🔢'}
          </button>
        </div>

        {/* Examples */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          flexWrap: 'wrap', 
          marginBottom: '15px'
        }}>
          {examples.map((ex, idx) => (
            <button
              key={idx}
              onClick={() => loadExample(ex.code)}
              style={{
                ...buttonStyle,
                background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                color: isDarkMode ? '#aaa' : '#666',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                fontSize: '12px',
                padding: '6px 14px'
              }}
              title={ex.desc}
            >
              📝 {ex.name}
            </button>
          ))}
        </div>

        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={textareaStyle}
          spellCheck="false"
        />

        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          margin: '15px 0', 
          flexWrap: 'wrap' 
        }}>
          <button
            onClick={runCode}
            disabled={isRunning}
            style={{
              ...buttonStyle,
              background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
              color: '#000',
              opacity: isRunning ? 0.6 : 1,
              boxShadow: '0 4px 20px rgba(74,222,128,0.3)'
            }}
            onMouseEnter={(e) => {
              if (!isRunning) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 30px rgba(74,222,128,0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isRunning) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 20px rgba(74,222,128,0.3)';
              }
            }}
          >
            {isRunning ? '⏳ გაშვება...' : '▶️ გაშვება'}
          </button>
          <button
            onClick={clearCode}
            style={{
              ...buttonStyle,
              background: 'linear-gradient(135deg, #f87171, #ef4444)',
              color: '#fff',
              boxShadow: '0 4px 20px rgba(248,113,113,0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 30px rgba(248,113,113,0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 20px rgba(248,113,113,0.3)';
            }}
          >
            🗑️ გასუფთავება
          </button>
          <button
            onClick={resetCode}
            style={{
              ...buttonStyle,
              background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              color: isDarkMode ? '#fff' : '#000',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
            }}
          >
            🔄 აღდგენა
          </button>
        </div>

        {score > 0 && (
          <div style={{
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
            borderRadius: '12px',
            color: '#000',
            marginBottom: '10px',
            textAlign: 'center',
            fontWeight: 'bold',
            animation: 'slideUp 0.3s ease-out'
          }}>
            {getScoreEmoji()} 🎯 ქულა: {score}/20
          </div>
        )}

        <div style={{ marginTop: '15px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '5px',
            flexWrap: 'wrap',
            gap: '5px'
          }}>
            <p style={{ color: isDarkMode ? '#aaa' : '#666', margin: 0, fontSize: 'clamp(12px, 1.5vw, 14px)' }}>
              📤 Output:
            </p>
            {output && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(output);
                  alert('✅ Output კოპირებულია!');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: isDarkMode ? '#666' : '#999',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                📋 კოპირება
              </button>
            )}
          </div>
          <div style={outputStyle}>
            {output || '💡 კოდი გაშვების შემდეგ აქ გამოჩნდება'}
          </div>
        </div>

        <div style={{ 
          marginTop: '15px', 
          fontSize: '12px', 
          color: isDarkMode ? '#555' : '#ccc',
          textAlign: 'center',
          borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
          paddingTop: '15px'
        }}>
          ⚠️ ყურადღება: კოდი გაშვებულია ბრაუზერში, არ გამოიყენოთ საშიში კოდი
        </div>
      </div>
    </div>
  );
};

export default CodeRunner;